import { api } from '@/lib/axios';

export interface LookupItem { code: string; description: string; }
/** Scan type for the scheduling attachment document-type picker (richer than LookupItem). */
export interface ScanTypeOption {
  code: string;
  displayName: string;
  ppUpload: boolean;
  documentLevel: string;
  ppDisplayOrder: number;
  displayOrder: number;
}
/** Patient-relation option with the row's same-as-patient rules: when a flag is true, that
 *  subscriber field should equal the patient's value for this relation. isSelf is the
 *  composite (all three flags 'Y' — the subscriber IS the patient). */
export interface PatientRelationItem {
  code: string;
  description: string;
  isSelf: boolean;
  firstNameSameAsPatient: boolean;
  lastNameSameAsPatient: boolean;
  birthDateSameAsPatient: boolean;
}
export interface SiteGroup { siteGroupCode: string; description: string; }
export interface Site { siteCode: string; name: string; address?: string; phone?: string; }
export interface ConfigItem { key: string; value: string | null; }
export interface SecurityQuestionOption { questionKey: number; questionText: string; }

/**
 * Resolved "experience" for a selected site group — Gen3 replacement for
 * Gen2's PortalSession.PersonalUserExperience. Drives theme, feature flags,
 * and practice-scoped behavior on the client.
 */
export interface SiteGroupExperience {
  siteGroupCode: string;
  description: string;
  practiceCode: string | null;
  /** pp_configuration_override JSON blob — theme, feature flags, phone, etc. */
  configOverride: Record<string, string>;
  /** portal_system_configuration_override JSON blob — system-level overrides. */
  systemConfigOverride: Record<string, unknown>;
  /** Windows timezone id from l_practice.time_zone_id (D8). */
  timeZoneId?: string | null;
  /** Practice-local wall clock at fetch (`yyyy-MM-ddTHH:mm:ss`) for clinic-day expiry (D8). */
  practiceLocalNow?: string | null;
}

export interface AppVersionInfo {
  apiVersion: string;
  dbVersion: string;
  dbName: string;
}

export interface SessionStateRequest {
  facilityId: string;
  languageCode: string;
}

export interface ValidationCheck {
  valid: boolean;
}

export interface SessionStateValidationResult {
  facility: ValidationCheck;
  language: ValidationCheck;
}

export interface MarketingDomainResult {
  siteGroupCode: string;
}

export const lookupService = {
  getSiteGroups: () => api.get<SiteGroup[]>('/Organization/SiteGroups').then(r =>
    Array.isArray(r.data) ? r.data : []),
  getSiteGroupExperience: (siteGroupCode: string) =>
    api.get<SiteGroupExperience>(`/Organization/SiteGroup/${encodeURIComponent(siteGroupCode)}/Experience`).then(r => r.data),
  getSites: () => api.get<Site[]>('/Organization/Sites').then(r => r.data),
  getUrgency: () => api.get<LookupItem[]>('/Lookups/Urgency').then(r => r.data),
  getLaterality: () => api.get<LookupItem[]>('/Lookups/Laterality').then(r => r.data),
  getBodyParts: () => api.get<LookupItem[]>('/Lookups/BodyPart').then(r => r.data),
  getExamTypes: () => api.get<LookupItem[]>('/Lookups/ExamTypes').then(r => r.data),
  getCarriers: () => api.get<LookupItem[]>('/Lookups/Carrier').then(r => r.data),
  getPatientRelations: () => api.get<PatientRelationItem[]>('/Lookups/PatientRelations').then(r => r.data),
  getScanTypes: () => api.get<LookupItem[]>('/Lookups/ScanTypes').then(r => r.data),
  getScanTypeOptions: () => api.get<ScanTypeOption[]>('/Lookups/ScanTypeOptions').then(r => r.data),
  getSystemConfig: () => api.get<ConfigItem[]>('/Lookups/SystemConfig').then(r => r.data),
  getStates: (country: string) => api.get<LookupItem[]>('/Lookups/States', { params: { country } }).then(r => r.data),
  getCountries: () => api.get<LookupItem[]>('/Lookups/Countries').then(r => r.data),
  getSecurityQuestions: (lang?: string) =>
    api.get<SecurityQuestionOption[]>('/Lookups/SecurityQuestions', { params: lang ? { lang } : undefined }).then(r => r.data),
  validateSessionState: (request: SessionStateRequest) =>
    api.post<SessionStateValidationResult>('/Organization/ValidateState', request).then(r => r.data),
  getVersion: () =>
    api.get<AppVersionInfo>('/Version').then(r => r.data),
  /**
   * Resolves a hostname against l_marketing_domain. Returns the matching
   * site_group_code when an active entry exists, or null when none matches.
   * Anonymous endpoint — safe to call before login.
   */
  getMarketingDomain: (domain: string) =>
    api.get<MarketingDomainResult | null>('/Organization/MarketingDomain', { params: { domain } })
      .then(r => r.data)
      .catch(() => null),
};
